/**
 * StewardDayPlanCard — "here's the day you just described; shall I write it down?"
 *
 * THE GAP THIS FILLS. A traveler typed out their own day — "8:00 Breakfast,
 * 9:00 leave the Staybridge, 9:30 Potter Marsh, … 8:30 PM flight", twelve stops
 * — and the product had nowhere to put it. The only itinerary rail was one
 * GROUNDED research card per venue, and "Breakfast" and "Drive back" can never
 * be grounded: there is no page to cite. So Steward could only talk about the
 * day. This card is the shape that holds it.
 *
 * WHAT IT IS NOT. It is not a research result. It carries no rating, no price,
 * no source and no map, because it asserts nothing about the world — every row
 * is the traveler's own sentence handed back for correction.
 *
 * PROPOSES, NEVER AUTO-WRITES. Each Add calls itineraryApi.createItem, the same
 * write path as the manual dialog, StewardCard and StewardBackboneCard. There
 * is no bulk endpoint and no server-side write: the loop lives here so a
 * partial failure leaves the rows that landed in place and re-offers only the
 * ones that did not.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Check, Loader2, Plus } from "lucide-react";
import { itineraryApi } from "@/lib/api";
import { ITEM_ICON, ITEM_LABEL } from "@/components/trips/itinerary/itinerary-utils";
import type { StewardDayPlanData, StewardDayStop } from "@/lib/types";
import {
  addButtonLabel,
  addOutcomeMessage,
  allIndexes,
  dayHeadline,
  dayPosture,
  selectedStops,
  stopTimeLabel,
  stopToItemInput,
  type AddOutcome,
} from "./stewardDay-utils";
import { invalidateAfterItineraryWrite } from "@/lib/itineraryCache";

export type DayAddState = "idle" | "adding" | "added";

interface RowProps {
  stop: StewardDayStop;
  checked: boolean;
  added: boolean;
  disabled: boolean;
  onToggle: () => void;
  inputId: string;
}

/**
 * One stop. The time sits first and in a fixed column so the day reads as a
 * schedule down the left edge; a stop with no time gets an em-dash rather than
 * a blank, so the column never collapses and an untimed stop still looks
 * deliberate instead of broken.
 */
export function DayStopRow({
  stop,
  checked,
  added,
  disabled,
  onToggle,
  inputId,
}: RowProps) {
  const Icon = ITEM_ICON[stop.item_type];

  return (
    <li className="flex items-start gap-3 rounded-lg border border-border bg-card p-2.5">
      <input
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        aria-label={`Include ${stop.title}`}
        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[hsl(var(--foreground))] disabled:opacity-50"
      />
      <span className="mt-px w-[4.75rem] shrink-0 font-mono text-[11px] leading-5 text-muted-foreground">
        {stopTimeLabel(stop)}
      </span>
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <label
            htmlFor={inputId}
            className="text-sm font-medium leading-snug text-foreground"
          >
            {stop.title}
          </label>
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {ITEM_LABEL[stop.item_type]}
          </span>
          {added && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-emerald-700">
              <Check className="h-3 w-3" aria-hidden="true" /> Added
            </span>
          )}
        </div>
        {(stop.location || stop.city) && (
          <p className="truncate text-xs text-muted-foreground">
            {[stop.location, stop.city].filter(Boolean).join(" · ")}
          </p>
        )}
        {stop.notes && (
          <p className="text-xs leading-snug text-muted-foreground/80">{stop.notes}</p>
        )}
      </div>
    </li>
  );
}

interface ViewProps {
  day: StewardDayPlanData;
  /** Indexes still ticked. */
  selected: readonly number[];
  /** Indexes already written to the itinerary. */
  addedIndexes: readonly number[];
  addState: DayAddState;
  /** The honest partial-failure line, or null when nothing failed. */
  outcomeMessage?: string | null;
  onToggle: (index: number) => void;
  onAdd: () => void;
}

/**
 * Presentational half — no data fetching, so the ordering, the copy and the
 * partial-failure wording can all be asserted without mounting anything.
 */
export function StewardDayPlanCardView({
  day,
  selected,
  addedIndexes,
  addState,
  outcomeMessage,
  onToggle,
  onAdd,
}: ViewProps) {
  const added = addState === "added";
  const busy = addState === "adding";
  // A stop already on the itinerary is not pending, even if it is still ticked.
  const count = selected.filter((i) => !addedIndexes.includes(i)).length;
  const partiallyAdded = addedIndexes.length > 0 && !added;

  return (
    <article
      data-testid="steward-dayplan"
      className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
    >
      <div className="space-y-3 p-3.5">
        <div className="flex items-start gap-3">
          <CalendarDays
            className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--primary))]"
            aria-hidden="true"
          />
          <div className="min-w-0 space-y-0.5">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Your day, as you described it
            </p>
            <h4 className="truncate text-sm font-semibold text-foreground">
              {dayHeadline(day)}
            </h4>
            {/* Says the posture out loud: a list that looks like an itinerary
                reads as one, and nothing here has been written yet. */}
            <p className="text-xs leading-snug text-muted-foreground">{dayPosture(day)}</p>
          </div>
        </div>

        {/* ORDER IS THE TRAVELER'S — rendered exactly as dictated, never sorted
            by time. They may have listed a 12:00 lunch after a 14:00 drive
            because that is the order they walk it. */}
        <ol className="space-y-1.5">
          {day.stops.map((stop, i) => (
            <DayStopRow
              key={i}
              inputId={`day-${day.day_date}-stop-${i}`}
              stop={stop}
              checked={selected.includes(i)}
              added={addedIndexes.includes(i)}
              disabled={busy || added || addedIndexes.includes(i)}
              onToggle={() => onToggle(i)}
            />
          ))}
        </ol>

        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          <button
            type="button"
            onClick={onAdd}
            disabled={added || busy || count === 0}
            aria-label={
              added
                ? `${dayHeadline(day)} added to itinerary`
                : addButtonLabel(day, count, partiallyAdded)
            }
            className={
              "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors motion-reduce:transition-none " +
              (added
                ? "cursor-default bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200"
                : "bg-[hsl(var(--foreground))] text-white hover:opacity-90 disabled:opacity-60")
            }
          >
            {busy ? (
              <>
                <Loader2
                  className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
                Adding
              </>
            ) : added ? (
              <>
                <Check className="h-3.5 w-3.5" aria-hidden="true" /> Added
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                {addButtonLabel(day, count, partiallyAdded)}
              </>
            )}
          </button>

          {/* Never "something went wrong". It says how many of how many landed,
              because the rows that did land are real and the ones that didn't
              are still on the list to try again. */}
          {outcomeMessage && (
            <span className="text-xs text-destructive">{outcomeMessage}</span>
          )}
        </div>
      </div>
    </article>
  );
}

interface Props {
  day: StewardDayPlanData;
  tripId: string;
}

/** Container: owns the tick state, the serial add loop and cache invalidation. */
export default function StewardDayPlanCard({ day, tripId }: Props) {
  const queryClient = useQueryClient();
  // Everything is ticked by default — the whole day IS the proposal. Unticking
  // is how a traveler drops one stop without losing the other eleven.
  const [selected, setSelected] = useState<number[]>(() => allIndexes(day));
  const [addedIndexes, setAddedIndexes] = useState<number[]>([]);
  const [outcome, setOutcome] = useState<AddOutcome | null>(null);

  const add = useMutation({
    mutationFn: async (): Promise<AddOutcome> => {
      const result: AddOutcome = { added: [], failed: [] };
      // Serial, and in the plan's order, on purpose: the itinerary's sort_order
      // is derived server-side, so parallel inserts would race for the day's
      // ordering and scramble the sequence the traveler dictated.
      const pending = selected.filter((i) => !addedIndexes.includes(i));
      for (const { index, stop } of selectedStops(day, pending)) {
        try {
          await itineraryApi.createItem(tripId, stopToItemInput(stop, day.day_date));
          result.added.push(index);
        } catch {
          result.failed.push(index);
        }
      }
      return result;
    },
    onSuccess: (result) => {
      setAddedIndexes((prev) => [...new Set([...prev, ...result.added])]);
      // Failures stay ticked so the next press retries exactly what did not
      // land. A stop the traveler had UNTICKED stays unticked and stays
      // available — it is a deferred decision, not a discarded one.
      setSelected((prev) => [
        ...new Set([...result.failed, ...prev.filter((i) => !result.added.includes(i))]),
      ]);
      setOutcome(result);
      // Everything that derives from the itinerary, including the gap banner —
      // a day that now has a plan is a day the banner must stop counting.
      invalidateAfterItineraryWrite(queryClient, tripId);
    },
  });

  /*
   * "Added" means the whole day is on the itinerary — NOT merely that the last
   * press ran clean. The difference is the traveler who unticks the museum
   * because they are still choosing between it and the Heritage Center, adds
   * the other eleven stops, then decides. If a clean press retired the card,
   * that stop could never be added from it again and the decision they
   * deliberately deferred would cost them the row.
   */
  const allAdded = addedIndexes.length === day.stops.length;
  const addState: DayAddState = allAdded ? "added" : add.isPending ? "adding" : "idle";

  return (
    <StewardDayPlanCardView
      day={day}
      selected={selected}
      addedIndexes={addedIndexes}
      addState={addState}
      outcomeMessage={
        add.isError
          ? "Couldn't add those — try again."
          : outcome
            ? addOutcomeMessage(outcome)
            : null
      }
      onToggle={(i) =>
        setSelected((prev) =>
          prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort((a, b) => a - b),
        )
      }
      onAdd={() => {
        // Idempotent at the UI level, same as a card — but the retiring
        // condition is the WHOLE day being on the itinerary, not one clean
        // press, so a stop the traveler deferred can still be added later.
        const pending = selected.filter((i) => !addedIndexes.includes(i));
        if (!allAdded && !add.isPending && pending.length > 0) {
          setOutcome(null);
          add.mutate();
        }
      }}
    />
  );
}
